import React from 'react';

import { useIntl } from 'react-intl-next';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { SearchGraphResultProps } from '../../../../../../../common/types';
import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { MentionedByLinkedResults } from '../../../../../../../common/ui/linked-results';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { enrichExcerpt } from '../../../../../../../common/utils/search-result-utils';

import type { searchGraphCustomerOrg_AtlassianSearchPage$key } from './__generated__/searchGraphCustomerOrg_AtlassianSearchPage.graphql';
import { messages } from './messages';

const styles = cssMap({
	subheading: {
		color: token('color.text.subtlest'),
		font: token('font.body'),
	},
});

export const SearchGraphCustomerOrg = ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	thirdPartyConfigs,
	mainContentWidth,
}: SearchGraphResultProps<searchGraphCustomerOrg_AtlassianSearchPage$key>): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const data = useFragment(
		graphql`
			fragment searchGraphCustomerOrg_AtlassianSearchPage on SearchResultGraphDocument
			@argumentDefinitions(
				isCustomerOrgOwnersEnabled: {
					type: "Boolean!"
					provider: "is-customer-org-owners-enabled.relayprovider"
				}
			) {
				__typename
				id
				title
				url
				lastModifiedDate
				entity {
					... on ExternalCustomerOrg {
						displayName
						contributors @skip(if: $isCustomerOrgOwnersEnabled) {
							thirdPartyUser {
								id
								name
							}
						}
						owners @include(if: $isCustomerOrgOwnersEnabled) {
							thirdPartyUser {
								name
							}
						}
						parent @optIn(to: "ExternalDataDepotQueryV2") {
							... on ExternalCustomerOrg {
								id
								displayName
							}
						}
						customerOrgLastActivity {
							lastActivityAt
							event
						}
					}
				}
				linkedResults {
					id
					title
					url
					type
					description
					lastModifiedDate
					category
					subtype
					integrationId
					providerId
					connectorType
				}
			}
		`,
		node,
	);

	const { id, url, title, lastModifiedDate, entity } = data;

	const lastModifiedComponent = <LastModified lastModified={lastModifiedDate} />;
	const body = <Stack>{entity?.customerOrgLastActivity?.event}</Stack>;

	const description = (
		<Box>
			<DescriptionBody description={body} />
			<Box xcss={styles.subheading}>
				<LastModified lastModified={entity?.customerOrgLastActivity?.lastActivityAt} />
			</Box>
		</Box>
	);

	const parent = entity?.parent?.displayName;

	const ownerFromContributors = entity?.contributors?.[0]?.thirdPartyUser?.name;

	const ownerFromOwners = () => {
		if (!entity?.owners || !Array.isArray(entity.owners) || entity.owners.length === 0) {
			return undefined;
		}
		return entity.owners[0]?.thirdPartyUser?.name;
	};

	const ownerName = ownerFromOwners() ?? ownerFromContributors;

	const headerMetadataParts = [
		formatMessage(messages.eventLabel),
		['parent', parent],
		['owner', ownerName],
		['last-modified', lastModifiedComponent],
	];

	const linkedResults = data.linkedResults?.map((linkedResult) => ({
		id: linkedResult.id,
		title: linkedResult.title,
		url: linkedResult.url,
		type: linkedResult.type,
		description: linkedResult.description,
		lastModifiedDate: linkedResult.lastModifiedDate,
		category: linkedResult.category,
		subtype: linkedResult.subtype,
		integrationId: linkedResult.integrationId,
		providerId: linkedResult.providerId,
		connectorType: linkedResult.connectorType,
	}));

	const footerComponent = (
		<MentionedByLinkedResults
			linkedResults={linkedResults || []}
			thirdPartyConfigs={thirdPartyConfigs}
			partial={data}
			url={url}
			position={position}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			isAllProductSelected={!selectedProduct}
		/>
	);

	return (
		<SearchResult
			id={id}
			product={product}
			productSelected={!!selectedProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			icon={icon}
			description={description}
			headerMetadataParts={headerMetadataParts}
			title={enrichExcerpt(title)}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={data}
			appMode={appMode}
			footer={footerComponent}
			hasRelatedLinks={!!linkedResults && linkedResults.length > 0}
			mainContentWidth={mainContentWidth}
		/>
	);
};
